import React, { useState, useEffect, useCallback } from 'react';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { translate } from 'components/Translation';
import SimpleTable from 'components/SimpleTable/SimpleTable';
import { zip } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { BodyS } from 'components/Typography/Typography';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/servicesDetailsTableStyle';
import { makeStyles } from '@material-ui/core';
import { env } from 'config/app.config';
import idConfig from 'utils/ecommerce/services/id.config';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IServiceTableProps {
	target: string;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IServiceDataProps {
	packageName: string;
	packageVersion: string;
	amount: string;
	currency: string;
}

const ServiceDetailsTable = ({ target, setLoading }: IServiceTableProps) => {
	const [services, setServices] = useState<(IServiceDataProps | null)[]>([]);
	const [currentServicesIds, setCurrentServicesIds] = useState<Array<string>>([]);
	const classes = useStyles();
	const [prepaidLoyaltyPackages] = useState(
		Object.entries(idConfig.PACKAGES.PREPAID_LOYALTY).map((value) => ({
			key: value[0],
			id: value[1][env],
		}))
	);

	useEffect(() => {
		const splitted = target ? target.split(',') : [];
		setCurrentServicesIds(splitted);
	}, [target]);

	const getType = useCallback(
		(parentCatalogGroupID: string[]) => {
			const groups = parentCatalogGroupID.map((group) => group.split('_')).flat();
			return prepaidLoyaltyPackages.find(({ id }) => groups.indexOf(id) >= 0);
		},
		[prepaidLoyaltyPackages]
	);

	useEffect(() => {
		if (currentServicesIds) {
			setLoading(true);
			zip(
				...(currentServicesIds || []).map((id) => {
					return ECommerceService.getContentByPartNumber(id);
				})
			)
				.pipe(
					finalize(() => {
						setLoading(false);
					})
				)
				.subscribe((res) => {
					const mappedServices: (IServiceDataProps | null)[] = (res || []).map((response) => {
						if (response.response.catalogEntryView && response.response.catalogEntryView.length > 0) {
							const data = response.response.catalogEntryView[0];
							if (data) {
								const parentCatalogGroupID = data.parentCatalogGroupID;
								const type = getType(parentCatalogGroupID);
								const price = (data.price || []).find(
									(singlePrice: { usage: string }) => singlePrice.usage === 'Display'
								);
								return {
									packageName: translate(`prepaid.loyalty.add.new.services.${type?.key || 'other'}`),
									packageVersion: data.name,
									amount: price ? price.value : '0',
									currency: price ? price.currency : translate('OMR'),
								};
							}
						}
						return null;
					});
					setServices(mappedServices);
					setLoading(false);
				});
		}
	}, [currentServicesIds, setLoading, getType]);

	return (
		<div className={classes.expander}>
			<SimpleTable
				data={services || []}
				striped={false}
				header={false}
				idAccessor="expanded-table"
				columns={[
					{
						header: '',
						// eslint-disable-next-line react/display-name
						cell: (row: IServiceDataProps) => (
							<>
								<div>
									<BodyS inline>
										<span className="expandWrapper">{row?.packageName}</span>
									</BodyS>{' '}
									<BodyS inline color="disabled-light">
										{row?.packageVersion}
									</BodyS>
								</div>
							</>
						),
					},
					{
						header: '',
						// eslint-disable-next-line react/display-name
						cell: (row: IServiceDataProps) => (
							<>
								<BodyS inline>
									{`${row?.amount} ${row?.currency} ${translate('prepaid.loyalty.common.per.line')}`}
								</BodyS>
							</>
						),
					},
				]}
				selectedItems={[]}
				setSelectedItems={() => null}
			/>
		</div>
	);
};

export default ServiceDetailsTable;
